import { CertificateUseCase } from '../usecases/certificate.usecase';
import { AnalyticsUseCase } from '../usecases/analytics.usecase';
import { CreateCertificateData, UpdateCertificateData, CertificateFilter, GetCertificateData } from '../repository/certificate.repository';

export class CertificateService {
  constructor(
    private certificateUseCase: CertificateUseCase,
    private analyticsUseCase?: AnalyticsUseCase
  ) {
    console.log('[CertificateService] Constructor - Dependencies injected');
  }

  // Create certificate with analytics tracking
  async createCertificate(certificateData: CreateCertificateData, requestingUserId?: string): Promise<GetCertificateData> {
    console.log('[CertificateService.createCertificate] START - Creating certificate');
    try {
      const certificate = await this.certificateUseCase.createCertificate(certificateData);
      
      // Track analytics
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent(
          'certificateCreated',
          requestingUserId || 'system',
          {
            certificateId: certificate.id,
            userId: certificate.userId,
            courseId: certificate.courseId,
            completionStatus: certificate.completionStatus,
            scorePercentage: certificate.scorePercentage,
            grade: certificate.grade
          }
        );
      }

      console.log('[CertificateService.createCertificate] SUCCESS - Certificate created with analytics tracking');
      return certificate;
    } catch (error) {
      console.error('[CertificateService.createCertificate] ERROR:', error);
      
      // Track error
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateCreationFailed',
          requestingUserId,
          { error: error instanceof Error ? error.message : 'Unknown error' }
        );
      }
      
      throw error;
    }
  }

  // Update certificate with analytics
  async updateCertificate(certificateId: string, updateData: UpdateCertificateData, requestingUserId?: string): Promise<GetCertificateData> {
    console.log('[CertificateService.updateCertificate] START - Updating certificate');
    try {
      const updatedCertificate = await this.certificateUseCase.updateCertificate(certificateId, updateData);
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateUpdated',
          requestingUserId,
          {
            certificateId: certificateId,
            updatedFields: Object.keys(updateData),
            completionStatus: updatedCertificate.completionStatus
          }
        );
      }

      console.log('[CertificateService.updateCertificate] SUCCESS - Certificate updated with analytics tracking');
      return updatedCertificate;
    } catch (error) {
      console.error('[CertificateService.updateCertificate] ERROR:', error);
      
      // Track error
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateUpdateFailed',
          requestingUserId,
          { certificateId: certificateId, error: error instanceof Error ? error.message : 'Unknown error' }
        );
      }
      
      throw error;
    }
  }

  // Get certificate by ID with analytics
  async getCertificateById(certificateId: string, requestingUserId?: string): Promise<GetCertificateData | null> {
    console.log('[CertificateService.getCertificateById] START - Getting certificate by ID');
    try {
      const certificate = await this.certificateUseCase.getCertificateById(certificateId, requestingUserId);
      
      // Track analytics for successful views
      if (certificate && this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateViewed',
          requestingUserId,
          {
            certificateId: certificateId,
            certificateNumber: certificate.certificateNumber,
            userId: certificate.userId
          }
        );
      }

      console.log('[CertificateService.getCertificateById] SUCCESS - Certificate retrieved');
      return certificate;
    } catch (error) {
      console.error('[CertificateService.getCertificateById] ERROR:', error);
      throw error;
    }
  }

  // Get certificates with analytics
  async getCertificates(filter: CertificateFilter = {}, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateService.getCertificates] START - Getting certificates');
    try {
      const certificates = await this.certificateUseCase.getCertificates(filter, requestingUserId, limitCount);
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificatesSearched',
          requestingUserId,
          {
            filterKeys: Object.keys(filter),
            resultCount: certificates.length,
            limit: limitCount
          }
        );
      }

      console.log('[CertificateService.getCertificates] SUCCESS - Certificates retrieved');
      return certificates;
    } catch (error) {
      console.error('[CertificateService.getCertificates] ERROR:', error);
      throw error;
    }
  }

  // Delete certificate with analytics
  async deleteCertificate(certificateId: string, requestingUserId?: string): Promise<void> {
    console.log('[CertificateService.deleteCertificate] START - Deleting certificate');
    try {
      await this.certificateUseCase.deleteCertificate(certificateId, requestingUserId);
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateDeleted',
          requestingUserId,
          { certificateId: certificateId }
        );
      }

      console.log('[CertificateService.deleteCertificate] SUCCESS - Certificate deleted with analytics tracking');
    } catch (error) {
      console.error('[CertificateService.deleteCertificate] ERROR:', error);
      
      // Track error
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateDeletionFailed',
          requestingUserId,
          { certificateId: certificateId, error: error instanceof Error ? error.message : 'Unknown error' }
        );
      }
      
      throw error;
    }
  }

  // Verify certificate with analytics
  async verifyCertificate(certificateNumber: string, requestingUserId?: string): Promise<{
    isValid: boolean;
    certificate?: GetCertificateData;
    reason?: string;
  }> {
    console.log('[CertificateService.verifyCertificate] START - Verifying certificate');
    try {
      const verificationResult = await this.certificateUseCase.verifyCertificate(certificateNumber);
      
      // Track analytics
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent(
          'certificateVerified',
          requestingUserId || 'anonymous',
          {
            certificateNumber: certificateNumber,
            isValid: verificationResult.isValid,
            reason: verificationResult.reason,
            certificateId: verificationResult.certificate?.id
          }
        );
      }

      console.log('[CertificateService.verifyCertificate] SUCCESS - Certificate verification completed');
      return verificationResult;
    } catch (error) {
      console.error('[CertificateService.verifyCertificate] ERROR:', error);
      
      // Track error
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent(
          'certificateVerificationFailed',
          requestingUserId || 'anonymous',
          { certificateNumber: certificateNumber, error: error instanceof Error ? error.message : 'Unknown error' }
        );
      }
      
      throw error;
    }
  }

  // Get user certificates with analytics
  async getUserCertificates(userId: string, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateService.getUserCertificates] START - Getting user certificates');
    try {
      const certificates = await this.certificateUseCase.getUserCertificates(userId, requestingUserId, limitCount);
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'userCertificatesViewed',
          requestingUserId,
          {
            viewedUserId: userId,
            certificateCount: certificates.length,
            limit: limitCount
          }
        );
      }

      console.log('[CertificateService.getUserCertificates] SUCCESS - User certificates retrieved');
      return certificates;
    } catch (error) {
      console.error('[CertificateService.getUserCertificates] ERROR:', error);
      throw error;
    }
  }

  // Get certificates by issuer with analytics
  async getCertificatesByIssuer(issuerId: string, requestingUserId?: string, limitCount?: number): Promise<GetCertificateData[]> {
    console.log('[CertificateService.getCertificatesByIssuer] START - Getting certificates by issuer');
    try {
      const certificates = await this.certificateUseCase.getCertificatesByIssuer(issuerId, requestingUserId, limitCount);
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'issuerCertificatesViewed',
          requestingUserId,
          {
            issuerId: issuerId,
            certificateCount: certificates.length,
            limit: limitCount
          }
        );
      }

      console.log('[CertificateService.getCertificatesByIssuer] SUCCESS - Issuer certificates retrieved');
      return certificates;
    } catch (error) {
      console.error('[CertificateService.getCertificatesByIssuer] ERROR:', error);
      throw error;
    }
  }

  // Generate certificate from completion with analytics
  async generateCertificateFromCompletion(
    userId: string,
    courseId: string,
    issuerId: string,
    completionData: {
      scorePercentage?: number;
      grade?: string;
      completionStatus: 'completed' | 'passed' | 'distinction';
      skillsEarned?: string[];
    },
    requestingUserId?: string
  ): Promise<GetCertificateData> {
    console.log('[CertificateService.generateCertificateFromCompletion] START - Generating certificate from completion');
    try {
      const certificate = await this.certificateUseCase.generateCertificateFromCompletion(
        userId,
        courseId,
        issuerId,
        completionData
      );
      
      // Track analytics
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent(
          'certificateGeneratedFromCompletion',
          requestingUserId || issuerId,
          {
            certificateId: certificate.id,
            userId: userId,
            courseId: courseId,
            completionStatus: completionData.completionStatus,
            scorePercentage: completionData.scorePercentage,
            grade: completionData.grade
          }
        );
      }

      console.log('[CertificateService.generateCertificateFromCompletion] SUCCESS - Certificate generated with analytics tracking');
      return certificate;
    } catch (error) {
      console.error('[CertificateService.generateCertificateFromCompletion] ERROR:', error);
      
      // Track error
      if (this.analyticsUseCase) {
        await this.analyticsUseCase.trackEvent(
          'certificateGenerationFailed',
          requestingUserId || issuerId,
          {
            userId: userId,
            courseId: courseId,
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        );
      }
      
      throw error;
    }
  }

  // Get certificate analytics with enhanced metrics
  async getCertificateAnalytics(issuerId?: string, requestingUserId?: string): Promise<{
    totalCertificates: number;
    completedCount: number;
    passedCount: number;
    distinctionCount: number;
    recentCertificates: GetCertificateData[];
    expiringThisMonth: number;
    monthlyTrends: { month: string; count: number }[];
    topCourses: { courseId: string; count: number }[];
  }> {
    console.log('[CertificateService.getCertificateAnalytics] START - Generating certificate analytics');
    try {
      const baseAnalytics = await this.certificateUseCase.getCertificateAnalytics(issuerId);
      
      // Get additional analytics data
      const allCertificates = await this.certificateUseCase.getCertificates(
        issuerId ? { issuedBy: issuerId } : {},
        requestingUserId
      );

      // Calculate monthly trends (last 12 months)
      const monthlyTrends: { month: string; count: number }[] = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        const count = allCertificates.filter(cert => {
          const certDate = cert.createdAt.toDate();
          return certDate.getFullYear() === date.getFullYear() && 
                 certDate.getMonth() === date.getMonth();
        }).length;
        monthlyTrends.push({ month: monthName, count });
      }

      // Calculate top courses by certificate count
      const courseCount: { [courseId: string]: number } = {};
      allCertificates.forEach(cert => {
        courseCount[cert.courseId] = (courseCount[cert.courseId] || 0) + 1;
      });
      
      const topCourses = Object.entries(courseCount)
        .map(([courseId, count]) => ({ courseId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const enhancedAnalytics = {
        ...baseAnalytics,
        monthlyTrends,
        topCourses
      };
      
      // Track analytics view
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'certificateAnalyticsViewed',
          requestingUserId,
          {
            issuerFilter: issuerId,
            totalCertificates: enhancedAnalytics.totalCertificates
          }
        );
      }

      console.log('[CertificateService.getCertificateAnalytics] SUCCESS - Enhanced analytics generated');
      return enhancedAnalytics;
    } catch (error) {
      console.error('[CertificateService.getCertificateAnalytics] ERROR:', error);
      throw error;
    }
  }

  // Get expiring certificates with notifications
  async getExpiringCertificates(daysAhead: number = 30, requestingUserId?: string): Promise<{
    certificates: GetCertificateData[];
    summary: {
      total: number;
      byStatus: { [status: string]: number };
      urgentCount: number; // expiring within 7 days
    };
  }> {
    console.log('[CertificateService.getExpiringCertificates] START - Getting expiring certificates');
    try {
      const expiringCertificates = await this.certificateUseCase.getExpiringCertificates(daysAhead, requestingUserId);
      
      // Calculate summary
      const byStatus: { [status: string]: number } = {};
      let urgentCount = 0;
      const urgentDate = new Date();
      urgentDate.setDate(urgentDate.getDate() + 7);

      expiringCertificates.forEach(cert => {
        byStatus[cert.completionStatus] = (byStatus[cert.completionStatus] || 0) + 1;
        
        if (cert.expiryDate && cert.expiryDate.toDate() <= urgentDate) {
          urgentCount++;
        }
      });

      const result = {
        certificates: expiringCertificates,
        summary: {
          total: expiringCertificates.length,
          byStatus,
          urgentCount
        }
      };
      
      // Track analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'expiringCertificatesChecked',
          requestingUserId,
          {
            daysAhead: daysAhead,
            totalExpiring: expiringCertificates.length,
            urgentCount: urgentCount
          }
        );
      }

      console.log('[CertificateService.getExpiringCertificates] SUCCESS - Expiring certificates retrieved');
      return result;
    } catch (error) {
      console.error('[CertificateService.getExpiringCertificates] ERROR:', error);
      throw error;
    }
  }

  // Bulk certificate operations
  async bulkIssueCertificates(
    certificatesData: CreateCertificateData[],
    requestingUserId?: string
  ): Promise<{
    successful: GetCertificateData[];
    failed: { data: CreateCertificateData; error: string }[];
  }> {
    console.log('[CertificateService.bulkIssueCertificates] START - Bulk issuing', certificatesData.length, 'certificates');
    try {
      const successful: GetCertificateData[] = [];
      const failed: { data: CreateCertificateData; error: string }[] = [];

      // Process certificates one by one to handle individual failures
      for (const certData of certificatesData) {
        try {
          const certificate = await this.createCertificate(certData, requestingUserId);
          successful.push(certificate);
        } catch (error) {
          failed.push({
            data: certData,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }
      
      // Track bulk operation analytics
      if (this.analyticsUseCase && requestingUserId) {
        await this.analyticsUseCase.trackEvent(
          'bulkCertificatesIssued',
          requestingUserId,
          {
            totalAttempted: certificatesData.length,
            successfulCount: successful.length,
            failedCount: failed.length
          }
        );
      }

      console.log('[CertificateService.bulkIssueCertificates] SUCCESS - Bulk operation completed');
      return { successful, failed };
    } catch (error) {
      console.error('[CertificateService.bulkIssueCertificates] ERROR:', error);
      throw error;
    }
  }
}